import React, { useState, useEffect } from 'react';
import { FileText, Code, Search, Database, Edit, History, Trash2, Plus, Save, X } from 'lucide-react';
import api from '../utils/api';

const Prompts = () => {
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingPrompt, setEditingPrompt] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    templateType: 'TEXT',
    templateConfiguration: { text: { text: '' } },
    modelId: 'anthropic.claude-3-sonnet-20240229-v1:0'
  });

  const mockPrompts = [
    {
      id: 1,
      title: 'Infrastructure CHG',
      subtitle: 'Change request review prompts',
      icon: 'file',
      description: 'You are a change management policy compliance reviewer. Review the following change request against organizational policies and provide a comprehensive assessment.\n\nIMPORTANT DATA INTERPRETATION RULES:\n1. A field is POPULATED if it contains any non-empty value\n2. Only mark a field as MISSING if it is...',
      version: 'v1.1',
      id_label: 'ID: change_review_prompt',
      updated: 'Updated: 11/1/2025'
    },
    {
      id: 2,
      title: 'Software SCR',
      subtitle: 'Software change request prompts',
      icon: 'code',
      description: '',
      version: '',
      id_label: '',
      updated: ''
    },
    {
      id: 3,
      title: 'ServiceNow Auto Review',
      subtitle: '',
      icon: 'file',
      description: 'You are a Change Management Policy Compliance Reviewer for EchoStar/DISH Network. Your role is to provide comprehensive, actionable policy compliance reviews for change requests that have been SUBMITTED FOR APPROVAL.\n\nCRITICAL OUTPUT REQUIREMENT: You MUST respond with ONLY a valid JSON object. Do no...',
      version: 'v1.511',
      id_label: 'ID: AI_Review',
      updated: 'Updated: 11/22/2025'
    },
    {
      id: 4,
      title: 'EntityID Finder',
      subtitle: 'Lambda-based entity matching',
      icon: 'search',
      description: '',
      version: '',
      id_label: '',
      updated: ''
    },
    {
      id: 5,
      title: 'Entity Matching',
      subtitle: '',
      icon: 'database',
      description: 'Match ServiceNow changes to the most likely Dynatrace entity',
      version: 'v2',
      id_label: '',
      updated: ''
    },
    {
      id: 6,
      title: 'Name Extraction',
      subtitle: '',
      icon: 'database',
      description: 'Extract Dynatrace-searchable service names from ServiceNow change requests',
      version: 'v2',
      id_label: '',
      updated: ''
    },
    {
      id: 7,
      title: 'Entity Type Selection',
      subtitle: '',
      icon: 'database',
      description: 'Analyze ServiceNow change and recommend relevant Dynatrace entity types',
      version: 'v2',
      id_label: '',
      updated: ''
    }
  ];

  useEffect(() => {
    fetchPrompts();
  }, []);

  const fetchPrompts = async () => {
    try {
      setLoading(true);
      const response = await api.getPrompts();
      setPrompts(response.prompts || []);
    } catch (error) {
      console.log('[VISION] Failed to fetch prompts:', error);
      setPrompts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      await api.createPrompt(formData);
      setShowCreateForm(false);
      setFormData({ name: '', description: '', templateType: 'TEXT', templateConfiguration: { text: { text: '' } }, modelId: 'anthropic.claude-3-sonnet-20240229-v1:0' });
      fetchPrompts();
    } catch (error) {
      alert('Failed to create prompt: ' + error.message);
    }
  };

  const handleUpdate = async (promptId) => {
    try {
      await api.updatePrompt(promptId, formData);
      setEditingPrompt(null);
      fetchPrompts();
    } catch (error) {
      alert('Failed to update prompt: ' + error.message);
    }
  };

  const handleDelete = async (promptId) => {
    if (!window.confirm('Are you sure you want to delete this prompt?')) return;
    
    try {
      await api.deletePrompt(promptId);
      fetchPrompts();
    } catch (error) {
      alert('Failed to delete prompt: ' + error.message);
    }
  };

  const startEdit = (prompt) => {
    setEditingPrompt(prompt.id);
    const variant = prompt.variants && prompt.variants[0];
    setFormData({
      name: prompt.name || prompt.title,
      description: prompt.description,
      templateType: variant?.templateType || 'TEXT',
      templateConfiguration: variant?.templateConfiguration || { text: { text: prompt.description } },
      modelId: variant?.modelId || 'anthropic.claude-3-sonnet-20240229-v1:0'
    });
  };

  const getIcon = (iconType) => {
    switch(iconType) {
      case 'file': return <FileText size={20} />;
      case 'code': return <Code size={20} />;
      case 'search': return <Search size={20} />;
      case 'database': return <Database size={20} />;
      default: return <FileText size={20} />;
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-96">
        <div className="text-lime-accent text-lg">Loading prompts...</div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-lime-accent" 
            style={{ textShadow: '0 0 20px #b2ff59, 0 0 40px #b2ff59' }}>
          Prompts
        </h1>
        <button 
          onClick={() => setShowCreateForm(true)}
          className="flex items-center gap-2 px-4 py-2 rounded bg-lime-accent/20 border border-lime-accent/40 text-lime-accent hover:bg-lime-accent/30 transition-all font-semibold"
          style={{ boxShadow: '0 0 15px rgba(178, 255, 89, 0.2)' }}>
          <Plus size={16} />
          New Prompt
        </button>
      </div>

      {showCreateForm && (
        <div className="bg-dark-card rounded-lg border border-lime-accent/40 p-6 space-y-4"
             style={{ background: '#0d0d0d', boxShadow: '0 0 30px rgba(178, 255, 89, 0.1)' }}>
          <h3 className="text-lg font-bold text-lime-accent">Create New Prompt</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input
              type="text"
              placeholder="Prompt Name"
              value={formData.name}
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="bg-dark-border/40 border border-dark-border/60 rounded px-3 py-2 text-white focus:border-lime-accent/40 focus:outline-none"
            />
            <select
              value={formData.modelId}
              onChange={(e) => setFormData({...formData, modelId: e.target.value})}
              className="bg-dark-border/40 border border-dark-border/60 rounded px-3 py-2 text-white focus:border-lime-accent/40 focus:outline-none">
              <option value="anthropic.claude-3-sonnet-20240229-v1:0">Claude 3 Sonnet</option>
              <option value="anthropic.claude-3-haiku-20240307-v1:0">Claude 3 Haiku</option>
              <option value="anthropic.claude-3-opus-20240229-v1:0">Claude 3 Opus</option>
            </select>
          </div>
          <input
            type="text"
            placeholder="Description"
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            className="w-full bg-dark-border/40 border border-dark-border/60 rounded px-3 py-2 text-white focus:border-lime-accent/40 focus:outline-none"
          />
          <textarea
            placeholder="Prompt Template"
            value={formData.templateConfiguration.text.text}
            onChange={(e) => setFormData({...formData, templateConfiguration: { text: { text: e.target.value } }})}
            rows={6}
            className="w-full bg-dark-border/40 border border-dark-border/60 rounded px-3 py-2 text-white focus:border-lime-accent/40 focus:outline-none font-mono text-sm"
          />
          <div className="flex gap-3">
            <button onClick={handleCreate} className="flex items-center gap-2 px-4 py-2 rounded bg-lime-accent/20 border border-lime-accent/40 text-lime-accent hover:bg-lime-accent/30 transition-all font-semibold">
              <Save size={16} />
              Create
            </button>
            <button onClick={() => setShowCreateForm(false)} className="flex items-center gap-2 px-4 py-2 rounded bg-dark-border/40 border border-dark-border/60 text-gray-400 hover:text-white transition-all">
              <X size={16} />
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {prompts.map((prompt) => (
          <div key={prompt.id} 
               className="bg-dark-card rounded-lg border border-dark-border p-6 hover:border-lime-accent/40 transition-all group"
               style={{ 
                 background: '#0d0d0d',
                 boxShadow: '0 0 30px rgba(178, 255, 89, 0.05), inset 0 0 40px rgba(0, 0, 0, 0.5)'
               }}>
            
            <div className="flex items-start gap-4 mb-4">
              <div className="w-12 h-12 rounded bg-purple-accent/20 border border-purple-accent/40 flex items-center justify-center flex-shrink-0 text-purple-accent"
                   style={{ boxShadow: '0 0 15px rgba(178, 75, 243, 0.2)' }}>
                {getIcon(prompt.icon)}
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="text-lg font-bold text-white">{prompt.title}</h3>
                  {prompt.version && (
                    <span className="px-2 py-0.5 rounded text-xs font-semibold bg-purple-accent/20 border border-purple-accent/40 text-purple-accent">
                      {prompt.version}
                    </span>
                  )}
                </div>
                {prompt.subtitle && (
                  <p className="text-sm text-gray-400">{prompt.subtitle}</p>
                )}
              </div>
            </div>

            {prompt.id_label && (
              <p className="text-xs text-gray-500 mb-2">{prompt.id_label}</p>
            )}

            {(prompt.description || (prompt.variants && prompt.variants[0]?.templateConfiguration?.text?.text)) && (
              <div className="bg-dark-border/40 border border-dark-border/60 rounded p-3 mb-4 font-mono text-xs text-gray-300 leading-relaxed max-h-32 overflow-hidden">
                {prompt.description || prompt.variants[0]?.templateConfiguration?.text?.text}
              </div>
            )}

            {prompt.updated && (
              <p className="text-xs text-gray-500 mb-4">{prompt.updated}</p>
            )}

            {editingPrompt === prompt.id ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="bg-dark-border/40 border border-dark-border/60 rounded px-3 py-2 text-white focus:border-lime-accent/40 focus:outline-none"
                  />
                  <select
                    value={formData.modelId}
                    onChange={(e) => setFormData({...formData, modelId: e.target.value})}
                    className="bg-dark-border/40 border border-dark-border/60 rounded px-3 py-2 text-white focus:border-lime-accent/40 focus:outline-none">
                    <option value="anthropic.claude-3-sonnet-20240229-v1:0">Claude 3 Sonnet</option>
                    <option value="anthropic.claude-3-haiku-20240307-v1:0">Claude 3 Haiku</option>
                    <option value="anthropic.claude-3-opus-20240229-v1:0">Claude 3 Opus</option>
                  </select>
                </div>
                <textarea
                  value={formData.templateConfiguration.text.text}
                  onChange={(e) => setFormData({...formData, templateConfiguration: { text: { text: e.target.value } }})}
                  rows={6}
                  className="w-full bg-dark-border/40 border border-dark-border/60 rounded px-3 py-2 text-white focus:border-lime-accent/40 focus:outline-none font-mono text-sm"
                />
                <div className="flex gap-3">
                  <button onClick={() => handleUpdate(prompt.id)} className="flex items-center gap-2 px-4 py-2 rounded bg-lime-accent/20 border border-lime-accent/40 text-lime-accent hover:bg-lime-accent/30 transition-all text-sm font-semibold">
                    <Save size={14} />
                    Save
                  </button>
                  <button onClick={() => setEditingPrompt(null)} className="flex items-center gap-2 px-4 py-2 rounded bg-dark-border/40 border border-dark-border/60 text-gray-400 hover:text-white transition-all text-sm">
                    <X size={14} />
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <button 
                  onClick={() => startEdit(prompt)}
                  className="flex items-center gap-2 px-4 py-2 rounded bg-dark-border/40 border border-lime-accent/40 text-lime-accent hover:bg-lime-accent/10 transition-all text-sm font-semibold"
                  style={{ boxShadow: '0 0 15px rgba(178, 255, 89, 0.15)' }}>
                  <Edit size={14} />
                  Edit
                </button>
                <button className="flex items-center gap-2 px-4 py-2 rounded bg-dark-border/40 border border-cyan-accent/40 text-cyan-accent hover:bg-cyan-accent/10 transition-all text-sm font-semibold">
                  <History size={14} />
                  History
                </button>
                <button 
                  onClick={() => handleDelete(prompt.id)}
                  className="ml-auto p-2 rounded bg-dark-border/40 border border-dark-border/60 text-gray-400 hover:text-red-500 hover:border-red-500/40 transition-all">
                  <Trash2 size={16} />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Prompts;
